import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import WebApp from '@twa-dev/sdk';
import { tasksApi } from '../lib/api';
import { useUserStore } from '../stores/userStore';
import { useLanguage } from '../hooks/useLanguage';
import { clsx } from 'clsx';
import { TranslationKey } from '../i18n/translations';

const PLATFORM_KEYS: Record<string, TranslationKey> = {
  reddit: 'platform_reddit',
  google_maps: 'platform_google_maps',
  instagram: 'platform_instagram',
  twitter: 'platform_twitter',
  app_store: 'platform_app_store',
  youtube: 'platform_youtube',
  tiktok: 'platform_tiktok',
  other: 'platform_other',
};

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUserStore();
  const { t } = useLanguage();

  const [generatedReview, setGeneratedReview] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasOpenedLink, setHasOpenedLink] = useState(false);

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: () => tasksApi.get(Number(id), user?.telegram_id),
    enabled: !!id,
  });

  const taskData = task as any;
  const hasClaimed = taskData?.claimed_by_user;
  const hasSubmitted = taskData?.user_submission;

  // Auto-claim when task loads and not claimed
  const claimMutation = useMutation({
    mutationFn: () => tasksApi.claim(Number(id), user!.telegram_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
  });

  // Auto-claim effect
  useEffect(() => {
    if (taskData && !hasClaimed && !hasSubmitted && taskData.slots_remaining > 0 && !claimMutation.isPending) {
      claimMutation.mutate();
    }
  }, [taskData, hasClaimed, hasSubmitted]);

  // Auto-generate review when claimed
  useEffect(() => {
    if (hasClaimed && !hasSubmitted && !generatedReview && !isGenerating && user) {
      generateReview();
    }
  }, [hasClaimed, hasSubmitted, generatedReview]);

  // Detect when user returns to app (after opening external link)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasOpenedLink && !showUpload) {
        setShowUpload(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasOpenedLink, showUpload]);

  const generateReview = async () => {
    if (!user) return;
    setIsGenerating(true);
    try {
      const result = await tasksApi.generateReview(Number(id), user.telegram_id);
      setGeneratedReview(result.review);
    } catch (error) {
      // Silent fail - user can retry
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyAndOpen = async () => {
    if (!generatedReview || !taskData) return;

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(generatedReview);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = generatedReview;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopied(true);
    setHasOpenedLink(true);

    // Open link after short delay so user sees "Copied!"
    setTimeout(() => {
      window.open(taskData.target_url, '_blank');
    }, 300);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 5 * 1024 * 1024) {
      WebApp.showAlert('Image too large. Max 5MB.');
      return;
    }

    setIsSubmitting(true);

    // Read as base64 and auto-submit
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await tasksApi.submit(Number(id), user.telegram_id, {
          proof_screenshot: reader.result as string,
        });
        queryClient.invalidateQueries({ queryKey: ['task', id] });
        WebApp.showAlert('Done! Your proof is submitted for review.');
      } catch (error) {
        WebApp.showAlert('Failed to submit. Try again.');
        setIsSubmitting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  if (isLoading || claimMutation.isPending) {
    return (
      <div className="p-4 space-y-4">
        <div className="bg-tg rounded-xl p-6 animate-pulse space-y-4">
          <div className="h-6 bg-tg-secondary rounded w-3/4" />
          <div className="h-4 bg-tg-secondary rounded w-1/2" />
        </div>
        <div className="text-center text-tg-hint text-sm">
          {claimMutation.isPending ? 'Starting task...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-4 text-center py-12">
        <p className="text-tg-hint">Task not found</p>
        <button onClick={() => navigate('/tasks')} className="mt-4 text-tg-link">
          {t('back')}
        </button>
      </div>
    );
  }

  const reward = (taskData.reward_usd / 100).toFixed(2);
  const platformLabel = PLATFORM_KEYS[taskData.platform] ? t(PLATFORM_KEYS[taskData.platform]) : taskData.platform;

  // Already submitted - show status
  if (hasSubmitted) {
    return (
      <div className="p-4 space-y-4">
        <button onClick={() => navigate(-1)} className="text-tg-link text-sm">
          {t('back')}
        </button>
        <div className="bg-tg rounded-xl p-6 text-center">
          <div className="text-3xl font-bold text-brand-primary mb-2">${reward}</div>
          <h1 className="text-lg font-bold text-tg mb-4">{taskData.title}</h1>
          <div
            className={clsx(
              'inline-block px-4 py-2 rounded-full text-sm font-medium',
              taskData.user_submission.status === 'approved' && 'bg-green-100 text-green-700',
              taskData.user_submission.status === 'rejected' && 'bg-red-100 text-red-700',
              taskData.user_submission.status === 'pending' && 'bg-yellow-100 text-yellow-700'
            )}
          >
            {taskData.user_submission.status === 'pending' ? 'Under Review' :
             taskData.user_submission.status === 'approved' ? 'Approved' : 'Rejected'}
          </div>
          {taskData.user_submission.review_notes && (
            <p className="mt-4 text-sm text-tg-hint">{taskData.user_submission.review_notes}</p>
          )}
        </div>
      </div>
    );
  }

  // No slots available
  if (taskData.slots_remaining <= 0) {
    return (
      <div className="p-4 space-y-4">
        <button onClick={() => navigate(-1)} className="text-tg-link text-sm">
          {t('back')}
        </button>
        <div className="bg-tg rounded-xl p-6 text-center">
          <h1 className="text-lg font-bold text-tg mb-2">{taskData.title}</h1>
          <p className="text-tg-hint">No slots available</p>
        </div>
      </div>
    );
  }

  // Upload screen
  if (showUpload) {
    return (
      <div className="p-4 space-y-4">
        <button onClick={() => setShowUpload(false)} className="text-tg-link text-sm">
          {t('back')}
        </button>

        <div className="bg-tg rounded-xl p-6 text-center">
          <h2 className="text-xl font-bold text-tg mb-2">Upload Proof</h2>
          <p className="text-tg-hint text-sm mb-6">Take a screenshot of your review</p>

          {isSubmitting ? (
            <div className="py-12">
              <div className="text-tg-hint">Submitting...</div>
            </div>
          ) : (
            <label className="block w-full py-16 border-2 border-dashed border-brand-primary rounded-xl text-center cursor-pointer active:bg-tg-secondary transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="text-brand-primary">
                <div className="text-4xl mb-3">+</div>
                <div className="font-medium">Tap to upload screenshot</div>
              </div>
            </label>
          )}
        </div>
      </div>
    );
  }

  // Main task view - streamlined
  return (
    <div className="p-4 space-y-4">
      <button onClick={() => navigate(-1)} className="text-tg-link text-sm">
        {t('back')}
      </button>

      {/* Header */}
      <div className="bg-tg rounded-xl p-5 text-center">
        <span className="text-xs text-tg-hint uppercase">{platformLabel}</span>
        <h1 className="text-lg font-bold text-tg mt-1">{taskData.title}</h1>
        <div className="text-3xl font-bold text-brand-primary mt-2">${reward}</div>
      </div>

      {/* Review Section */}
      <div className="bg-tg rounded-xl p-5">
        {isGenerating ? (
          <div className="text-center py-8">
            <div className="text-tg-hint">Generating review...</div>
          </div>
        ) : generatedReview ? (
          <div className="space-y-4">
            <div className="bg-tg-secondary rounded-xl p-4 text-tg text-sm leading-relaxed">
              {generatedReview}
            </div>

            <button
              onClick={handleCopyAndOpen}
              className="w-full py-4 rounded-xl bg-brand-primary text-white font-bold text-lg"
            >
              {copied ? 'Copied! Opening...' : 'Copy & Open ' + platformLabel}
            </button>

            <button
              onClick={generateReview}
              className="w-full py-2 text-tg-hint text-sm"
            >
              Generate different review
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <button
              onClick={generateReview}
              className="text-tg-link"
            >
              Generate review
            </button>
          </div>
        )}
      </div>

      {/* Manual upload option */}
      {generatedReview && (
        <button
          onClick={() => setShowUpload(true)}
          className="w-full py-3 text-tg-hint text-sm"
        >
          Already posted? Upload screenshot
        </button>
      )}
    </div>
  );
}
